// Body Scan provider adapter — the seam where photo-scan vendors plug in.
//
// v1 manual entry stays first-class; the 3DLOOK FitXpress integration is now
// REAL behind this interface. Photos flow: capture (client) → base64 →
// body-scan-process edge function (the ONLY holder of THREEDLOOK_API_KEY) →
// FitXpress API → normalized result. Photos are NEVER persisted on our side
// (product/GDPR decision 03/07) — they exist in memory during the call only.
//
// Flags (both required for a REAL scan — quota is ~100-200 req/MONTH):
//   client: VITE_BODY_SCAN_PROVIDER === "3dlook" selects ThreeDLookProvider
//           (anything else → MockScanProvider, the dev default: zero quota).
//   server: BODY_SCAN_REAL_ENABLED === "true" on the edge function, else it
//           refuses before any vendor call.
//   UI:     VITE_BODY_SCAN_ENABLED === "true" shows the scan entry at all.

import { supabase } from "@/integrations/supabase/client";

export const BODY_SCAN_PROVIDER_ENABLED =
  (import.meta.env.VITE_BODY_SCAN_ENABLED as string | undefined) === "true";

// Subset of body_measurements the scan flow fills. cm/kg (metric-only v1).
export interface ScanMeasurements {
  weight_kg?: number | null;
  body_fat_pct?: number | null;
  neck_cm?: number | null;
  shoulders_cm?: number | null;
  chest_cm?: number | null;
  waist_cm?: number | null;
  hips_cm?: number | null;
  bicep_left_cm?: number | null;
  bicep_right_cm?: number | null;
  thigh_left_cm?: number | null;
  thigh_right_cm?: number | null;
  calf_left_cm?: number | null;
  calf_right_cm?: number | null;
}

export interface ScanComposition {
  fat_percentage?: number | null;
  bmi?: number | null;
  bmr?: number | null;
  fat_body_mass?: number | null;
  lean_body_mass?: number | null;
  estimated_weight?: number | null;
}

export interface ScanInput {
  gender: "male" | "female";
  heightCm: number;
  weightKg?: number | null;
  age?: number | null;
  /** data-URI base64 ("data:image/jpeg;base64,..."). Never persisted. */
  frontPhotoBase64: string;
  sidePhotoBase64: string;
}

export interface ScanError {
  error_source: "front_photo" | "side_photo" | null;
  detail: string;
}

export interface ScanResult {
  status: "successful" | "failed";
  measurements: ScanMeasurements;
  /** Full provider payload (~90 keys, decision 03/07: keep ALL) → extra_measurements jsonb. */
  raw: Record<string, unknown>;
  provider: string;
  /** FitXpress measurement uuid — persisted as provider_scan_id (phase-2 Body Progress). */
  scanId: string | null;
  modelUrl: string | null;
  composition: ScanComposition | null;
  bodyType: string | null;
  /** Per-photo retake guidance when status === "failed". */
  errors?: ScanError[];
  /** Vendor quota snapshot when the call fetched it. */
  quota?: Record<string, unknown> | null;
}

export type ScanSession = { scanId: string };

export interface BodyScanProvider {
  id: string;
  capabilities: {
    photosRequired: number;
    returnsBodyFat: boolean;
    returnsFullPayload: boolean;
  };
  startScan(input: ScanInput): Promise<ScanSession>;
  getResult(session: ScanSession): Promise<ScanResult>;
  // ── Phase 2 (declared, NOT implemented — see PR report) ────────────────
  /** FitXpress POST /measurements/<id>/predict_model/ — 3D model at target weight. */
  getFutureBody?(scanId: string, targetWeightKg: number): Promise<{ modelUrl: string }>;
  /** FitXpress POST /body_progress/ — morph between two stored scans. */
  getBodyProgress?(beforeScanId: string, afterScanId: string): Promise<{ modelUrl: string }>;
}

// ── Mock provider — dev default. NEVER touches the vendor (zero quota). ─────
export class MockScanProvider implements BodyScanProvider {
  id = "mock";
  capabilities = { photosRequired: 2, returnsBodyFat: true, returnsFullPayload: true };
  private last: ScanResult | null = null;

  async startScan(input: ScanInput): Promise<ScanSession> {
    await new Promise((r) => setTimeout(r, 1200)); // simulated processing
    const jitter = (base: number, spread: number) =>
      Math.round((base + (Math.random() - 0.5) * spread) * 10) / 10;
    const weight = input.weightKg ?? jitter(78, 2);
    const measurements: ScanMeasurements = {
      weight_kg: weight,
      body_fat_pct: jitter(19, 1.5),
      neck_cm: jitter(38, 1),
      shoulders_cm: jitter(44, 2),
      chest_cm: jitter(100, 2),
      waist_cm: jitter(84, 2),
      hips_cm: jitter(98, 2),
      bicep_left_cm: jitter(34, 1),
      bicep_right_cm: jitter(34, 1),
      thigh_left_cm: jitter(58, 1.5),
      thigh_right_cm: jitter(58, 1.5),
      calf_left_cm: jitter(38, 1),
      calf_right_cm: jitter(38, 1),
    };
    const composition: ScanComposition = {
      fat_percentage: measurements.body_fat_pct,
      bmi: jitter(24, 1),
      bmr: Math.round(jitter(1700, 100)),
      fat_body_mass: jitter(15, 2),
      lean_body_mass: jitter(63, 2),
      estimated_weight: weight,
    };
    // Raw payload mirrors the 3dlook shape so the grouped UI renders in dev.
    this.last = {
      status: "successful",
      measurements,
      raw: {
        provider: this.id,
        mock: true,
        composition,
        circumference_params: { neck: measurements.neck_cm, chest: measurements.chest_cm, waist: measurements.waist_cm, low_hips: measurements.hips_cm, bicep: measurements.bicep_left_cm, thigh: measurements.thigh_left_cm, calf: measurements.calf_left_cm, abdomen: jitter(86, 2), knee: jitter(38, 1), ankle: jitter(23, 1), wrist: jitter(17, 1), forearm: jitter(28, 1), high_hips: jitter(92, 2), body_type: "triangle" },
        front_linear_params: { shoulders: measurements.shoulders_cm, inseam: jitter(78, 2), body_height: input.heightCm, sleeve_length: jitter(60, 2) },
        side_linear_params: { waist_depth: jitter(22, 1), neck_to_chest: jitter(20, 1) },
      },
      provider: this.id,
      scanId: "mock-" + crypto.randomUUID(),
      modelUrl: null,
      composition,
      bodyType: "triangle",
    };
    return { scanId: this.last.scanId as string };
  }

  async getResult(_session: ScanSession): Promise<ScanResult> {
    if (!this.last) throw new Error("no scan started");
    return this.last;
  }
}

// ── 3DLOOK FitXpress provider — REAL. Every startScan burns vendor quota. ───
// All vendor traffic goes through body-scan-process; this class never sees
// the token. In-function polling usually finishes the scan in one invoke;
// a "pending" answer falls back to client-side re-polls (action: "poll").
export class ThreeDLookProvider implements BodyScanProvider {
  id = "3dlook";
  capabilities = { photosRequired: 2, returnsBodyFat: true, returnsFullPayload: true };
  private results = new Map<string, ScanResult>();

  private toResult(data: Record<string, unknown>): ScanResult | "pending" {
    const status = String(data.status || "");
    if (status === "pending") return "pending";
    if (status === "failed") {
      return {
        status: "failed",
        measurements: {},
        raw: {},
        provider: this.id,
        scanId: (data.scan_id as string) ?? null,
        modelUrl: null,
        composition: null,
        bodyType: null,
        errors: (data.errors as ScanError[]) || [],
        quota: (data.quota as Record<string, unknown>) ?? null,
      };
    }
    const raw = (data.raw || {}) as Record<string, unknown>;
    return {
      status: "successful",
      measurements: (data.measurements || {}) as ScanMeasurements,
      raw,
      provider: this.id,
      scanId: (data.scan_id as string) ?? null,
      modelUrl: (data.model_3d_url as string) ?? null,
      composition: (raw.composition as ScanComposition) ?? null,
      bodyType: (raw.body_type as string) ?? null,
      quota: (data.quota as Record<string, unknown>) ?? null,
    };
  }

  async startScan(input: ScanInput): Promise<ScanSession> {
    const { data, error } = await supabase.functions.invoke("body-scan-process", {
      body: {
        action: "scan",
        gender: input.gender,
        height: input.heightCm,
        weight: input.weightKg ?? undefined,
        age: input.age ?? undefined,
        front_photo: input.frontPhotoBase64,
        side_photo: input.sidePhotoBase64,
      },
    });
    if (error) {
      console.error("[ThreeDLookProvider] scan invoke failed", error);
      throw error;
    }
    const d = data as Record<string, unknown>;
    const scanId = (d.scan_id as string) || "unknown";
    const mapped = this.toResult(d);
    if (mapped !== "pending") this.results.set(scanId, mapped);
    return { scanId };
  }

  async getResult(session: ScanSession): Promise<ScanResult> {
    const cached = this.results.get(session.scanId);
    if (cached) return cached;
    // Scan outlived the edge function's internal polling window — re-poll.
    // Only GETs on the vendor side (no new measurement is created).
    for (let i = 0; i < 20; i++) {
      const { data, error } = await supabase.functions.invoke("body-scan-process", {
        body: { action: "poll", scan_id: session.scanId },
      });
      if (error) {
        console.error("[ThreeDLookProvider] poll invoke failed", error);
        throw error;
      }
      const mapped = this.toResult(data as Record<string, unknown>);
      if (mapped !== "pending") {
        this.results.set(session.scanId, mapped);
        return mapped;
      }
      await new Promise((r) => setTimeout(r, 4000));
    }
    throw new Error("scan timed out — try again later (scan_id: " + session.scanId + ")");
  }

  // Phase 2 — architecture ready (scan_id persisted per measurement), UI and
  // edge actions intentionally NOT implemented yet. See PR "Fase 2" section.
  async getFutureBody(_scanId: string, _targetWeightKg: number): Promise<{ modelUrl: string }> {
    throw new Error("Future Body is phase 2 — not implemented");
  }
  async getBodyProgress(_beforeScanId: string, _afterScanId: string): Promise<{ modelUrl: string }> {
    throw new Error("Body Progress is phase 2 — not implemented");
  }
}

// Provider selection: explicit env opt-in for the real vendor; Mock otherwise.
export function getActiveScanProvider(): BodyScanProvider {
  const which = (import.meta.env.VITE_BODY_SCAN_PROVIDER as string | undefined) || "mock";
  return which === "3dlook" ? new ThreeDLookProvider() : new MockScanProvider();
}
